// Browser-side API client + TanStack Query hooks. Field names follow spec/04 exactly.
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AcceptanceCriterion, ClarifyingQuestion, CriterionResult, DiffStat, ExecutionSettings, FeatureOrigin, PhaseApprovalSummary, PlanOrigin, QuestionAnswers, SyncState, TaskStatus, TaskTier, TaskVerification, TechStack } from './types'

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message)
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json: body, ...rest } = init
  const res = await fetch(path, {
    ...rest,
    headers: { ...(body !== undefined ? { 'content-type': 'application/json' } : {}), ...(rest.headers ?? {}) },
    body: body !== undefined ? JSON.stringify(body) : rest.body,
    credentials: 'same-origin',
  })
  if (!res.ok) {
    let msg = res.statusText
    let code: string | undefined
    try {
      const j = await res.json()
      msg = j?.error?.message ?? msg
      code = j?.error?.code
    } catch {}
    throw new ApiError(res.status, msg, code)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

// ---------- types (response shapes)
export interface PlanDto {
  id: string
  userId: string
  idea: string
  language: 'en' | 'id'
  title: string
  techStack: TechStack
  questionAnswers: QuestionAnswers | null
  syncState: SyncState | null
  step: number
  status: 'draft' | 'generating' | 'completed' | 'error'
  promptCopied: boolean
  workspaceId: string
  workspaceName: string
  hasWiki: boolean
  cycle: number
  continuesPlanId: string | null
  /** Proof-of-Done execution settings; null = { mode: 'autopilot', verifier: 'local' } */
  execution?: ExecutionSettings | null
  /** per-plan model override (model id or 'auto'); null = user default */
  model?: string | null
  /** Proof-of-Why: set when the plan was handed off from a Design Room */
  origin?: PlanOrigin | null
  /** non-tech F0: 'simple' = Rencana Ringkas (plain language, ≤ 15 steps); 'full' = default PRD */
  summaryMode?: 'full' | 'simple'
  createdAt: string
  updatedAt: string
}
export interface DocumentDto {
  id: string
  planId: string
  title: string
  content: string
  version: number
  model?: string | null
  createdAt: string
}
export interface PlanFull {
  plan: PlanDto
  documents: DocumentDto[]
  chatMessages: unknown[]
  attachments: unknown[]
}
export interface FeatureDto {
  id: string
  planId: string
  workspaceId: string
  parentId: string | null
  featureId?: string | null
  name: string
  summary: string | null
  icon: string | null
  status: string
  priority: string | null
  phase: number
  spec: string | null
  isDraft: boolean
  source: 'ai' | 'user'
  position: number
  /** Proof-of-Why: room cards / hypotheses this feature answers (null = team addition or no room) */
  origin?: FeatureOrigin | null
  createdAt: string
  updatedAt: string
}
export interface TaskDto {
  id: string
  planId: string
  featureId: string
  title: string
  description: string
  status: TaskStatus
  priority: 'high' | 'medium' | 'low'
  layer: 'frontend' | 'backend'
  page: string | null
  prompt: string
  pasted: boolean
  log: unknown[]
  position: number
  ref: string
  /** ids of tasks that must be done first (Fase 2) */
  dependsOn: string[]
  parallelSafe: boolean
  /** derived server-side: unfinished prerequisites (task-level + prerequisite features) */
  blockedBy: string[]
  // ---- Proof-of-Done (moat plan) — read defensively, older cached rows may lack them
  acceptance?: AcceptanceCriterion[]
  verification?: TaskVerification
  tier?: TaskTier
  ownedFiles?: string[]
  lastEvidence?: LastEvidenceDto | null
  // ---- non-tech F0: plain-language labels, only set on simple plans
  plainTitle?: string | null
  plainDone?: string | null
  /** Inspirasi F4: id of the task this one was auto-created to fix */
  followUpOf?: string | null
  createdAt: string
  updatedAt: string
}
export interface LastEvidenceDto { id: string; status: 'pass' | 'fail' | 'override'; attempt: number; createdAt: string; passed: number; total: number; verifier: string; overrideReason: string | null; reviewerNote: string | null }
export interface TaskEvidenceDto {
  id: string
  taskId: string
  planId: string
  attempt: number
  agent: string | null
  model: string | null
  status: 'pass' | 'fail' | 'override'
  criteriaResults: CriterionResult[]
  commitHash: string | null
  diffStat: DiffStat | null
  filesTouched: string[] | null
  screenshots: string[] | null
  durationMs: number | null
  inputTokens: number | null
  outputTokens: number | null
  costUsd: string | null
  overrideReason: string | null
  verifier: string
  reviewerNote: string | null
  createdAt: string
}
export interface PhaseApprovalDto { id: string; planId: string; phase: number; layer: string; approvedBy: string; approvedById: string | null; summary: PhaseApprovalSummary; createdAt: string }
export interface CheckpointDto { phase: number; layer: 'frontend' | 'backend'; taskRef?: string; summary: PhaseApprovalSummary }
export interface FeatureDependencyDto {
  id: string
  planId: string
  fromFeatureId: string
  toFeatureId: string
  kind: 'blocks' | 'relates'
  source: 'ai' | 'user'
  createdAt: string
}
export interface PlanListItem {
  id: string
  title: string
  idea: string
  step: number
  status: string
  createdAt: string
  updatedAt: string
  workspaceId: string
  workspaceName: string
  cycle: number
  hasWiki: boolean
  canCreatePlan: boolean
}
export interface WorkspacePlans {
  workspace: { id: string; name: string; slug: string; codebaseStatus: string }
  plans: { id: string; title: string; idea: string; cycle: number; status: string; step: number; continuesPlanId: string | null; totalTasks: number; doneTasks: number; updatedAt: string; state: string }[]
  nextCycle: number
  hasWiki: boolean
  canCreatePlan: boolean
  blocker: string | null
}
export interface SubscriptionStatus {
  planType: string
  limits: { prdPerMonth: number; chatMessagesPerMonth: number; premiumAddons: boolean; pdfDownloadEnabled: boolean; revisionChatEnabled: boolean; learningContentAccess: string; coachingSessionsPerMonth: number; allowedModels?: string[]; premiumWeightPerMonth?: number }
  /** model selector: what this user may pick + premium pool usage + live availability */
  models: { allowed: string[]; default: string; preferred: string | null; premium: { used: number; limit: number }; unavailable: Record<string, { reason: 'quota' | 'errors'; until: string | null }> }
  prdUsage: { used: number; limit: number; addonCredits: number }
  chatUsage: { used: number; limit: number; addonCredits: number }
  coachingUsage: { used: number; limit: number }
  addonCredits: { prdCredits: number; chatCredits: number; coachingCredits: number; hasHistory: boolean }
  subscription: { id: string; status: string; startsAt: string; expiresAt: string; plan: PricingPlanDto } | null
}
export interface PricingPlanDto {
  id: string
  name: string
  slug: string
  description: string | null
  price: number
  currency: 'IDR' | 'USD'
  durationDays: number
  features: string[]
  limits: SubscriptionStatus['limits']
  isActive: boolean
  sortOrder: number
}
export interface JobPayload {
  phase: 'idle' | 'prd' | 'review' | 'skeleton' | 'enrich' | 'done'
  status: 'idle' | 'running' | 'done' | 'error'
  kind?: 'prd' | 'features' | 'full' | 'rebuild'
  totalFeatures: number
  doneFeatures: number
  error?: string
  /** model selector: requested id ('auto' allowed) and what was actually used ("a→b (reason)" on downgrade) */
  model?: string | null
  modelUsed?: string | null
}

// ---------- query keys
export const qk = {
  plan: (id: string) => ['plan', id] as const,
  plans: ['plans'] as const,
  features: (id: string) => ['features', id] as const,
  subFeatures: (id: string) => ['subfeatures', id] as const,
  tasks: (id: string) => ['tasks', id] as const,
  featureDeps: (id: string) => ['feature-deps', id] as const,
  wsPlans: (id: string) => ['workspace', id, 'plans'] as const,
  subscription: ['subscription', 'status'] as const,
  pricing: ['pricing'] as const,
}

// ---------- hooks
export const usePlan = (id: string, opts: { refetchInterval?: number | false } = {}) =>
  useQuery({ queryKey: qk.plan(id), queryFn: () => api<PlanFull>(`/api/plan/${id}`), ...opts })
export const usePlans = () => useQuery({ queryKey: qk.plans, queryFn: () => api<{ plans: PlanListItem[] }>('/api/plan/list') })
export const useFeatures = (planId: string, enabled = true) =>
  useQuery({ queryKey: qk.features(planId), queryFn: () => api<{ features: FeatureDto[] }>(`/api/plan/features/list?planId=${planId}`), enabled })
export const useSubFeatures = (planId: string, enabled = true) =>
  useQuery({ queryKey: qk.subFeatures(planId), queryFn: () => api<{ subFeatures: FeatureDto[] }>(`/api/plan/subfeatures/list?planId=${planId}`), enabled })
export const useFeatureDeps = (planId: string, enabled = true) =>
  useQuery({ queryKey: qk.featureDeps(planId), queryFn: () => api<{ dependencies: FeatureDependencyDto[] }>(`/api/plan/features/deps?planId=${planId}`), enabled })
export const useTasks = (planId: string, opts: { enabled?: boolean; refetchInterval?: number | false } = {}) =>
  useQuery({ queryKey: qk.tasks(planId), queryFn: () => api<{ tasks: TaskDto[] }>(`/api/plan/tasks/list?planId=${planId}`), refetchInterval: opts.refetchInterval ?? 5000, enabled: opts.enabled ?? true })

/**
 * Live task updates over SSE (`/api/plan/tasks/stream`): pushes each `tasks` event straight into the
 * tasks query cache. Returns `false` while the stream is down so callers can fall back to polling.
 */
export function useTaskStream(planId: string, enabled = true) {
  const qc = useQueryClient()
  const [live, setLive] = useState(false)
  useEffect(() => {
    if (!enabled || !planId || typeof EventSource === 'undefined') return
    let es: EventSource | null = null
    let retry: ReturnType<typeof setTimeout> | undefined
    let closed = false
    const open = () => {
      es = new EventSource(`/api/plan/tasks/stream?planId=${planId}`)
      es.addEventListener('tasks', (ev) => {
        try {
          const data = JSON.parse((ev as MessageEvent<string>).data) as { tasks: TaskDto[] }
          qc.setQueryData(qk.tasks(planId), data)
          setLive(true)
        } catch {
          /* ignore malformed frame */
        }
      })
      es.onopen = () => setLive(true)
      es.onerror = () => {
        setLive(false)
        es?.close()
        es = null
        if (!closed) retry = setTimeout(open, 10_000)
      }
    }
    open()
    return () => {
      closed = true
      if (retry) clearTimeout(retry)
      es?.close()
      setLive(false)
    }
  }, [planId, enabled, qc])
  return live
}
export const useWorkspacePlans = (id: string) => useQuery({ queryKey: qk.wsPlans(id), queryFn: () => api<WorkspacePlans>(`/api/workspaces/${id}/plans`), refetchInterval: 8000 })
export const useSubscription = (enabled = true) => useQuery({ queryKey: qk.subscription, queryFn: () => api<SubscriptionStatus>('/api/subscription/status'), enabled, staleTime: 30_000 })
export const usePricing = () => useQuery({ queryKey: qk.pricing, queryFn: () => api<{ plans: PricingPlanDto[] }>('/api/payments/plans'), staleTime: 300_000 })

export function useUpdatePlan(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (patch: Partial<Pick<PlanDto, 'techStack' | 'questionAnswers' | 'step' | 'title' | 'language' | 'promptCopied'>>) => api<{ plan: PlanDto }>(`/api/plan/${id}`, { method: 'PATCH', json: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.plan(id) }),
  })
}
export function useTaskStatus(planId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ taskIds, status }: { taskIds: string[]; status: TaskStatus }) =>
      api<{ updated: number }>('/api/plan/tasks/bulk-status', { method: 'PATCH', json: { planId, taskIds, status } }),
    onMutate: async ({ taskIds, status }) => {
      await qc.cancelQueries({ queryKey: qk.tasks(planId) })
      const prev = qc.getQueryData<{ tasks: TaskDto[] }>(qk.tasks(planId))
      if (prev) qc.setQueryData(qk.tasks(planId), { tasks: prev.tasks.map((t) => (taskIds.includes(t.id) ? { ...t, status } : t)) })
      return { prev }
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(qk.tasks(planId), ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.tasks(planId) }),
  })
}

export type { ClarifyingQuestion }
